import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePerspective } from '../contexts/PerspectiveContext';
import { getAdminParentPreviewId } from '../services/api';
import { logger } from '../utils/logger';

/**
 * ClientRouteWrapper
 * Wraps client routes to allow access for:
 * 1. Actual client users (userType === 'CLIENT')
 * 2. Parent admins (userType === 'PARENT_ADMIN') -- they use the same
 *    client portal as regular clients; the backend aggregates data
 *    across every child clinic under their parent_company so a single
 *    /client/dashboard request shows the union.
 * 3. Internal admins in switched client view (perspective === 'CLIENT')
 * 4. Parent admins in switched child-clinic view (their own perspective
 *    switch into one of their children for action surfaces like
 *    placing an order that needs a single ship-to context)
 */
const ClientRouteWrapper = ({ children, currentUser, isLoading = false }) => {
  const { isInSwitchedView, perspectiveContext, isLoading: perspectiveLoading } = usePerspective();

  // Normalize user type to uppercase for comparison
  const userType = currentUser?.userType?.toUpperCase();

  // Debug logging
  logger.log('ClientRouteWrapper check:', {
    currentUser: currentUser ? { id: currentUser.id, userType: currentUser.userType } : null,
    userType,
    isLoading,
    perspectiveLoading,
    isInSwitchedView,
    currentPerspective: perspectiveContext?.currentPerspective,
    targetUserId: perspectiveContext?.targetUserId,
  });

  // Show loading state while session is being restored
  // This prevents premature redirects during page reload
  if (isLoading) {
    logger.log('ClientRouteWrapper: Session loading, showing placeholder');
    return <div style={{ padding: 16 }} />;
  }

  // If we have perspective state indicating switched view, wait for user to load
  // This handles the race condition on page reload
  if (isInSwitchedView && !currentUser) {
    logger.log('ClientRouteWrapper: Perspective exists but no user yet, showing placeholder');
    return <div style={{ padding: 16 }} />;
  }

  // If perspective is still loading and we're an admin-style user, wait.
  // This prevents redirects while the perspective context is being verified.
  if (perspectiveLoading && (userType === 'INTERNAL' || userType === 'PARENT_ADMIN')) {
    logger.log('ClientRouteWrapper: Perspective loading for admin/parent user, showing placeholder');
    return <div style={{ padding: 16 }} />;
  }

  // Allow if user is a client
  if (userType === 'CLIENT') {
    logger.log('ClientRouteWrapper: Allowing access - user is CLIENT');
    return children;
  }

  // Allow parent admins through directly. They use the same /client/*
  // portal pages as regular clients; the backend aggregates data
  // across every child clinic under their parent_company.
  if (userType === 'PARENT_ADMIN') {
    logger.log('ClientRouteWrapper: Allowing access - user is PARENT_ADMIN');
    return children;
  }

  // Internal admin previewing a parent admin's portal -- the View As
  // button on the Parent Companies row stamps an
  // adminPreviewParentCompanyId in sessionStorage and drops the admin
  // here. Backend honors the override only for INTERNAL callers.
  if (userType === 'INTERNAL' && getAdminParentPreviewId()) {
    logger.log('ClientRouteWrapper: Allowing access - INTERNAL admin in parent preview');
    return children;
  }

  // Allow if admin/parent admin is in switched client view.
  // currentPerspective should be 'CLIENT' for both flows.
  const currentPerspective = perspectiveContext?.currentPerspective?.toUpperCase();
  if (
    (userType === 'INTERNAL' || userType === 'PARENT_ADMIN') &&
    isInSwitchedView &&
    currentPerspective === 'CLIENT'
  ) {
    logger.log('ClientRouteWrapper: Allowing access - switched CLIENT view', { userType });
    return children;
  }

  // Additional check: target user is a CLIENT but currentPerspective is
  // missing or weird. Same as above but more permissive on the perspective
  // field (defensive against legacy session rows).
  if (
    (userType === 'INTERNAL' || userType === 'PARENT_ADMIN') &&
    isInSwitchedView &&
    perspectiveContext?.targetUserId &&
    perspectiveContext?.targetUserType?.toUpperCase() === 'CLIENT'
  ) {
    logger.log('ClientRouteWrapper: Allowing access - switched view with CLIENT target', { userType });
    return children;
  }

  // Otherwise redirect to the appropriate landing for the caller.
  if (userType === 'INTERNAL') {
    logger.log('ClientRouteWrapper: Internal user without client perspective - redirecting to /org-select');
    return <Navigate to="/org-select" replace />;
  }

  logger.log('ClientRouteWrapper: Denying access - redirecting to /');
  return <Navigate to="/" replace />;
};

export default ClientRouteWrapper;
